/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { or } from '@ember/object/computed';
import AuthService from 'cio/services/auth';
import { MergeAttributeListArgs } from 'cio/components/action-composer/context-viewer/merge-attribute-list';
import { isJSONAttribute, toJSONAttribute } from 'cio/utils/attributes';
import { keyPathToString, stringToPath } from 'cio/utils/objects';
import { isPresent } from '@ember/utils';

type MergeAttributeListItemArgs = Omit<MergeAttributeListArgs, 'attributes'> & {
  attribute: MergeAttributeListArgs['attributes'][number];
  filterAttributeChanged: boolean;
  hasReadOnlyPermission: boolean;
  noJsonParsing?: boolean;
};

export default class Item extends Component<MergeAttributeListItemArgs> {
  @service auth!: AuthService;

  @or('args.attribute.label', 'args.attribute') label!: string;

  @computed('_label', 'highlightAttribute', 'filterAttribute', 'filterAttributeChanged')
  get attributeClasses() {
    let classes = [];
    if (this.args.highlightAttribute && this.args.filterAttribute === this.label) {
      classes.push('bg-info');

      if (this.args.filterAttributeChanged) {
        classes.push('is-new');
      }
    }

    return classes.join(' ');
  }

  get canAddNestedJSONKeys() {
    // Disable adding nested keys in data actions until
    // https://github.com/customerio/issues/issues/7420
    // is resolved
    if (this.args.editableEntity.action != null) {
      let { action } = this.args.editableEntity;

      return (
        !action.isCreateEventAction && !action.isBatchUpdateAction && !action.isCreateOrUpdatePersonAttributeAction
      );
    }

    return true;
  }

  get liquidString() {
    if (this.args.liquidPrefix != null) {
      return keyPathToString([this.args.liquidPrefix, this.label]);
    }

    if (this.args.liquidPath != null) {
      return keyPathToString([...this.args.liquidPath, this.label]);
    }

    return this.label;
  }

  get hasValue() {
    return isPresent(this.args.attribute.value);
  }

  get noJsonParsing() {
    return this.args.noJsonParsing === true;
  }

  get isJSON() {
    if (this.noJsonParsing) {
      return false;
    }

    return isJSONAttribute(this.args.attribute.value);
  }

  get jsonValue() {
    if (!this.isJSON) {
      return null;
    }

    try {
      return toJSONAttribute(this.args.attribute.value);
    } catch {
      return null;
    }
  }

  @action
  insertLiquid(event: MouseEvent) {
    // Only accept main (left) mouse click
    if (event.button !== 0) {
      return;
    }

    this.args.insertLiquid(event, this.liquidString);
  }

  @action
  onItemClick(value: string, event: Event) {
    let path = stringToPath(value);

    if (this.args.liquidPrefix != null) {
      this.args.insertLiquid(event, keyPathToString([this.args.liquidPrefix, this.label, ...path]));
    }

    if (this.args.liquidPath != null) {
      this.args.insertLiquid(event, keyPathToString([...this.args.liquidPath, this.label, ...path]));
    }
  }
}
